/**
 * Guide Signup Flow Container
 *
 * Main container component orchestrating the 5-step guide signup process.
 * Restructured from 3-stage to 5-step flow (SCRUM-1862).
 *
 * Epic: MVP 1.2.1 - Guide Signup Flow
 * Location: src/components/guides/signup/GuideSignupFlow.jsx
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Header from '../../layout/Header';
import Footer from '../../layout/Footer';
import { getCachedPreLaunchEnabled } from '../../../utils/preLaunchConfigCache';
import PreSignupValue from './PreSignupValue';
import SignupProgress from './SignupProgress';
import Stage1CoreIdentity from './Stage1CoreIdentity';
import Step2VerifyIdentity from './Step2VerifyIdentity';
import Step3ServiceInfo from './Step3ServiceInfo';
import Step4Documents from './Step4Documents';
import Step5Complete from './Step5Complete';
import { useAuth } from '../../../contexts/AuthContext';

// Frontend (CloudFront/S3) and API live on separate hosts in non-local envs
// (staging: env-9b2d → env-7f3a; production: www → api). Bare same-origin
// /api/* paths get served the SPA index.html by CloudFront's SPA fallback and
// fail JSON.parse. Always prefix VITE_API_URL; fall back to '' so the local
// dev proxy (vite.config) still handles /api/* in development.
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * GuideSignupFlow Container Component
 *
 * @param {Object} props
 * @param {boolean} props.isOAuth - Whether user is signing up via OAuth
 * @param {Object} props.oauthData - Pre-filled data from OAuth provider
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element}
 */
const GuideSignupFlow = ({ isOAuth = false, oauthData = {}, className = '' }) => {
  const { setTokenFromCallback, isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // State
  const [currentStep, setCurrentStep] = useState(0); // 0 = pre-signup value, 1-5 = steps
  const [formData, setFormData] = useState({
    isOAuth,
    ...oauthData
  });
  const [guideId, setGuideId] = useState(null);
  const [error, setError] = useState('');
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const autoResumeAttempted = useRef(false);

  /**
   * Handle starting signup flow
   */
  const handleGetStarted = () => {
    setCurrentStep(1);
  };

  /**
   * Handle ?resume=TOKEN on mount — POST /api/v1/guides/signup/resume, store tokens, navigate
   */
  useEffect(() => {
    const resumeToken = searchParams.get('resume');
    if (!resumeToken) { return; }

    setResumeLoading(true);
    fetch(`${API_BASE_URL}/api/v1/guides/signup/resume`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resumeToken }),
    })
      .then((res) => res.json())
      .then(async (data) => {
        if (!data.success) {
          const errorMessages = {
            TOKEN_EXPIRED: 'This resume link has expired. Please request a new one.',
            TOKEN_USED: 'This resume link has already been used.',
            TOKEN_NOT_FOUND: 'Invalid resume link.',
          };
          setError(errorMessages[data.error] || 'Unable to resume your application.');
          setResumeLoading(false);
          return;
        }

        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('tokenIssuedAt', String(Date.now()));
        await setTokenFromCallback(data.accessToken);

        if (data.guideId) { setGuideId(data.guideId); }

        // Fetch user profile so email/phone are available for Step 2 OTP.
        // Capture values first, then call all state setters synchronously so
        // React 18 batches them into one render — prevents Step 2 mounting with
        // an empty email prop before formData is updated.
        let capturedMe = null;
        await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${data.accessToken}` },
        })
          .then((r) => r.ok ? r.json() : null)
          .then((meData) => { capturedMe = meData; })
          .catch(() => {});

        // All state updates in one synchronous block — batched into a single render
        setFormData(prev => ({
          ...prev,
          email: prev.email || capturedMe?.data?.user?.email || '',
          phone: prev.phone || capturedMe?.data?.user?.phone || '',
        }));
        setError('');
        navigate('/guides/signup', { replace: true });
        setCurrentStep(data.resumeStage + 1);
        setResumeLoading(false);
      })
      .catch(() => {
        setError('Unable to resume your application. Please try again.');
        setResumeLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * SCRUM-1975: Auto-resume for authenticated users.
   * Skip if resume token is present (it takes precedence).
   */
  useEffect(() => {
    if (authLoading || autoResumeAttempted.current) { return; }
    if (searchParams.get('resume')) { return; }
    if (!isAuthenticated) { return; }

    autoResumeAttempted.current = true;

    const token = localStorage.getItem('accessToken');
    if (!token) { return; }

    setResumeLoading(true);
    fetch(`${API_BASE_URL}/api/v1/dashboard/onboarding-progress`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!data?.success) {
          setResumeLoading(false);
          return;
        }

        const stage = data.data?.signupStage ?? 0;

        if (data.data?.signupComplete) {
          navigate('/guide/dashboard', { replace: true });
          return;
        }

        if (stage > 0) {
          // Capture meData first; call all state setters in .finally() so
          // React 18 batches them into one render (prevents Step 2 mounting
          // with an empty email prop before formData is populated).
          let capturedAutoMe = null;
          fetch(`${API_BASE_URL}/api/v1/auth/me`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
          })
            .then((r) => r.ok ? r.json() : null)
            .then((meData) => { capturedAutoMe = meData; })
            .catch(() => {})
            .finally(() => {
              if (capturedAutoMe?.data?.user?.id) { setGuideId(capturedAutoMe.data.user.id); }
              setFormData(prev => ({
                ...prev,
                email: prev.email || capturedAutoMe?.data?.user?.email || '',
                phone: prev.phone || capturedAutoMe?.data?.user?.phone || '',
              }));
              setError('');
              setCurrentStep(stage + 1);
              setResumeLoading(false);
            });
        } else {
          setResumeLoading(false);
        }
      })
      .catch(() => {
        setResumeLoading(false);
      });
  }, [authLoading, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Scroll tracking for sticky CTA
   */
  useEffect(() => {
    if (currentStep !== 0) { return; }

    const handleScroll = () => {
      // Show sticky CTA after scrolling 40% down the page
      const scrollPercentage = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      setShowStickyCTA(scrollPercentage > 40);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentStep]);

  /**
   * Handle form data update
   * @param {Object} updates - Fields to update
   */
  const handleUpdate = (updates) => {
    if (updates.guideId) { setGuideId(updates.guideId); }
    setFormData((prev) => ({
      ...prev,
      ...updates
    }));
  };

  /**
   * Handle navigation to previous step.
   * Back navigation is blocked at step 4+ — once Step 3 service info is submitted, cannot go back.
   * When SMS consent was not granted, step 2 is skipped so back from step 3 goes to step 1.
   */
  const handleBack = () => {
    if (currentStep >= 4) { return; }
    const prevStep = currentStep === 3 && !formData.smsConsentGranted ? 1 : currentStep - 1;
    setCurrentStep(prevStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Render current view based on step
   */
  const renderCurrentView = () => {
    // Pre-signup value proposition — show resume errors above the landing page
    if (currentStep === 0) {
      return (
        <>
          {error && (
            <div className="max-w-5xl mx-auto px-4 pt-6">
              <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-semibold text-red-800">Error</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <PreSignupValue
            onGetStarted={handleGetStarted}
            className="py-12"
          />
        </>
      );
    }

    // Signup steps
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Progress Indicator */}
        <SignupProgress
          currentStep={currentStep}
          className="mb-10"
        />

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="text-sm font-semibold text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {currentStep === 1 && (
            <Stage1CoreIdentity
              formData={formData}
              onUpdate={handleUpdate}
              onNext={() => setCurrentStep(2)}
              onBack={handleBack}
              isOAuth={isOAuth}
            />
          )}

          {currentStep === 2 && (
            <Step2VerifyIdentity
              phone={formData.phone}
              email={formData.email}
              smsConsentGranted={formData.smsConsentGranted ?? false}
              onNext={() => setCurrentStep(3)}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && (
            <Step3ServiceInfo
              formData={formData}
              onUpdate={handleUpdate}
              onNext={() => setCurrentStep(4)}
              onBack={handleBack}
            />
          )}

          {currentStep === 4 && (
            <Step4Documents guideId={guideId} onNext={() => setCurrentStep(5)} />
          )}

          {currentStep === 5 && (
            <Step5Complete />
          )}
        </div>

        {/* Help Section */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Need help?{' '}
            <a
              href="/support"
              className="text-emerald-600 hover:text-emerald-700 font-medium underline"
            >
              Contact Support
            </a>
            {' '}or{' '}
            <a
              href="/guides/faq"
              className="text-emerald-600 hover:text-emerald-700 font-medium underline"
            >
              View FAQ
            </a>
          </p>
        </div>
      </div>
    );
  };

  if (resumeLoading) {
    return (
      <div className={`min-h-screen bg-gray-50 flex items-center justify-center ${className}`}>
        <p className="text-gray-600">Resuming your application…</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${className}`}>
      {/* Hide Header when pre-launch gate is ON — PreLaunchLayout provides its own */}
      {!getCachedPreLaunchEnabled() && (
        <Header
          showStickyCTA={currentStep === 0 && showStickyCTA}
          onCtaClick={handleGetStarted}
        />
      )}

      {/* Mobile Sticky CTA - Bottom Bar (only on mobile) */}
      {currentStep === 0 && showStickyCTA && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40 animate-slide-up">
          <div className="px-4 py-3">
            <button
              onClick={handleGetStarted}
              className="w-full flex items-center justify-center px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
            >
              Create Your Profile
              <ChevronRight className="ml-2 w-4 h-4" />
            </button>
            <p className="text-xs text-center text-gray-600 mt-2">
              15-20 minutes • No credit card • Free to join
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main>
        {/* Step Indicator Banner (only show during steps 1-5) */}
        {currentStep > 0 && currentStep <= 5 && (
          <div className="bg-white border-b border-gray-200 py-3">
            <div className="max-w-5xl mx-auto px-4 flex items-center justify-between">
              <span className="text-sm text-gray-600 font-medium">
                Step {currentStep} of 5
              </span>
              <button
                onClick={() => {
                  // TODO: Implement save draft functionality
                  setError('Draft saved! You can continue later from your dashboard.');
                }}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Save & Exit
              </button>
            </div>
          </div>
        )}

        {renderCurrentView()}
      </main>

      {/* Reuse existing Footer component */}
      <Footer />
    </div>
  );
};

export default GuideSignupFlow;
